import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Eye, Edit, Trash2, MapPin, X, Loader, Star } from 'lucide-react';
import { useSnackbar } from '../../contexts/SnackbarContext';
import api from '../../utils/api';
import { getDishDisplayName } from '../../utils/dishDisplay';
import './RestaurantList.scss';

const RestaurantList = () => {
    const { success, error, confirm } = useSnackbar();
    const navigate = useNavigate();
    const [restaurants, setRestaurants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        page: 1,
        limit: 20,
        search: '',
        district: '',
        status: '',
        verified: ''
    });
    const [pagination, setPagination] = useState({});
    const [extractingCoordinates, setExtractingCoordinates] = useState(false);
    const [fetchingRatings, setFetchingRatings] = useState(false);

    useEffect(() => {
        fetchRestaurants();
    }, [filters]);

    const fetchRestaurants = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            Object.keys(filters).forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });

            const response = await api.get(`/admin/restaurants?${params.toString()}`);
            if (response.data.success) {
                setRestaurants(response.data.restaurants);
                setPagination(response.data.pagination);
            }
        } catch (error) {
            console.error('Error fetching restaurants:', error);
            error('Error fetching restaurants');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (restaurantID) => {
        confirm(
            'Are you sure you want to delete this restaurant? This action cannot be undone.',
            async () => {
                try {
                    await api.delete(`/admin/restaurants/${restaurantID}`);
                    success('Restaurant deleted successfully');
                    fetchRestaurants();
                } catch (err) {
                    error(err.response?.data?.message || 'Error deleting restaurant');
                }
            }
        );
    };

    const handleExtractCoordinates = async (restaurantID) => {
        try {
            setExtractingCoordinates(true);
            const response = await api.post('/admin/restaurants/extract-coordinates', { restaurantID });
            if (response.data.success) {
                success(`Coordinates extracted successfully: ${response.data.coordinates.latitude.toFixed(6)}, ${response.data.coordinates.longitude.toFixed(6)}`);
                fetchRestaurants();
            }
        } catch (err) {
            error(err.response?.data?.message || 'Error extracting coordinates');
        } finally {
            setExtractingCoordinates(false);
        }
    };

    const handleFetchRatings = async (restaurantID) => {
        try {
            setFetchingRatings(true);
            const response = await api.post(`/admin/restaurants/${restaurantID}/fetch-ratings`);
            if (response.data.success) {
                success(`Ratings updated: ${response.data.rating ? response.data.rating.toFixed(1) : 'N/A'} stars (${response.data.reviewCount} reviews)`);
                fetchRestaurants();
            }
        } catch (err) {
            error(err.response?.data?.message || 'Error fetching ratings. Make sure Google Place ID is set and GOOGLE_PLACES_API_KEY is configured.');
        } finally {
            setFetchingRatings(false);
        }
    };

    const hasCoordinates = (restaurant) => {
        return restaurant.location?.latitude && restaurant.location?.longitude;
    };

    const districts = [
        'Alappuzha', 'Ernakulam', 'Idukki', 'Kannur', 'Kasaragod',
        'Kollam', 'Kottayam', 'Kozhikode', 'Malappuram', 'Palakkad',
        'Pathanamthitta', 'Thiruvananthapuram', 'Thrissur', 'Wayanad'
    ];

    return (
        <div className="restaurant-list">
            <div className="page-header">
                <h2>Restaurants</h2>
                <div className="header-actions">
                    <button className="add-button" onClick={() => navigate('/restaurants/add')}>
                        <Plus size={18} />
                        Add Restaurant
                    </button>
                </div>
            </div>

            <div className="filters">
                <div className="search-box">
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search restaurants..."
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                    />
                </div>

                <select
                    value={filters.district}
                    onChange={(e) => setFilters({ ...filters, district: e.target.value, page: 1 })}
                >
                    <option value="">All Districts</option>
                    {districts.map(district => (
                        <option key={district} value={district}>{district}</option>
                    ))}
                </select>

                <select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
                >
                    <option value="">All Status</option>
                    <option value="available">Available</option>
                    <option value="unavailable">Unavailable</option>
                </select>

                <select
                    value={filters.verified}
                    onChange={(e) => setFilters({ ...filters, verified: e.target.value, page: 1 })}
                >
                    <option value="">All</option>
                    <option value="true">Verified</option>
                    <option value="false">Unverified</option>
                </select>
            </div>

            {loading ? (
                <div className="loading">Loading restaurants...</div>
            ) : (
                <>
                    <div className="table-container">
                        <table className="restaurants-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Name</th>
                                    <th>District</th>
                                    <th>Dish</th>
                                    <th>Status</th>
                                    <th>Verified</th>
                                    <th>Premium</th>
                                    <th>Rating</th>
                                    <th>Coordinates</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {restaurants.length === 0 ? (
                                    <tr>
                                        <td colSpan="10" className="no-data">
                                            No restaurants found
                                        </td>
                                    </tr>
                                ) : (
                                    restaurants.map((restaurant) => (
                                        <tr key={restaurant._id}>
                                            <td>{restaurant.restaurantID}</td>
                                            <td>{restaurant.name}</td>
                                            <td>{restaurant.district}</td>
                                            <td>{getDishDisplayName(restaurant.dish)}</td>
                                            <td>
                                                <span className={`status-badge ${restaurant.status}`}>
                                                    {restaurant.status}
                                                </span>
                                            </td>
                                            <td>
                                                {restaurant.verified ? (
                                                    <span className="verified-badge">Yes</span>
                                                ) : (
                                                    <span className="unverified-badge">No</span>
                                                )}
                                            </td>
                                            <td>
                                                {restaurant.premium ? (
                                                    <span className="premium-badge">⭐ Premium</span>
                                                ) : (
                                                    <span>-</span>
                                                )}
                                            </td>
                                            <td>
                                                {restaurant.googleRating ? (
                                                    <span className="rating-badge" title={`${restaurant.googleRating.toFixed(1)} stars, ${restaurant.googleReviewCount} reviews`}>
                                                        <Star size={14} fill="#fbbf24" color="#fbbf24" />
                                                        {restaurant.googleRating.toFixed(1)} ({restaurant.googleReviewCount})
                                                    </span>
                                                ) : (
                                                    <span className="rating-badge missing">No rating</span>
                                                )}
                                            </td>
                                            <td>
                                                {hasCoordinates(restaurant) ? (
                                                    <span className="coords-badge success" title={`${restaurant.location.latitude.toFixed(6)}, ${restaurant.location.longitude.toFixed(6)}`}>
                                                        ✓ Has Coords
                                                    </span>
                                                ) : (
                                                    <span className="coords-badge missing">Missing</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button
                                                        className="action-btn view"
                                                        onClick={() => navigate(`/restaurants/${restaurant.restaurantID}`)}
                                                        title="View"
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                    <button
                                                        className="action-btn edit"
                                                        onClick={() => navigate(`/restaurants/${restaurant.restaurantID}/edit`)}
                                                        title="Edit"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                    {restaurant.googlePlaceId && (
                                                        <button
                                                            className="action-btn rating"
                                                            onClick={() => handleFetchRatings(restaurant.restaurantID)}
                                                            disabled={fetchingRatings}
                                                            title="Fetch Google Ratings"
                                                        >
                                                            {fetchingRatings ? (
                                                                <Loader size={16} className="spinning" />
                                                            ) : (
                                                                <Star size={16} />
                                                            )}
                                                        </button>
                                                    )}
                                                    {!hasCoordinates(restaurant) && restaurant.googleMapsLink && (
                                                        <button
                                                            className="action-btn extract"
                                                            onClick={() => handleExtractCoordinates(restaurant.restaurantID)}
                                                            disabled={extractingCoordinates}
                                                            title="Extract Coordinates"
                                                        >
                                                            {extractingCoordinates ? (
                                                                <Loader size={16} className="spinning" />
                                                            ) : (
                                                                <MapPin size={16} />
                                                            )}
                                                        </button>
                                                    )}
                                                    <button
                                                        className="action-btn delete"
                                                        onClick={() => handleDelete(restaurant.restaurantID)}
                                                        title="Delete"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {pagination.pages > 1 && (
                        <div className="pagination">
                            <button
                                disabled={filters.page === 1}
                                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                            >
                                Previous
                            </button>
                            <span>
                                Page {pagination.page} of {pagination.pages}
                            </span>
                            <button
                                disabled={filters.page === pagination.pages}
                                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default RestaurantList;
